import { selectClient, selectIllustrationResponseDataByQuoteId } from '@client/model/selectors';
import { AppState } from '@client/model/store';
import {
  createQuoteWithROIandProjectedCoverage,
  createTraditionalLTCQuoteWithROIandProjectedCoverage,
  createUpdatedQuoteFromResponse,
} from '@client/util/policyQuoteCalculations';
import { getQuoteId, isTraditionalLTCQuote } from '@client/util/quoteTypeGuards';
import { RefreshCw } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { InsuranceQuoteComparison } from './InsuranceQuoteComparison';
import { TraditionalLTCQuoteComparison } from './TraditionalLTCQuoteComparison';

const parseMonetaryValue = (value: string | null): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/[$,]/g, '')) || 0;
};

type IllustrationResponseData = { [key: string]: string | null } | null;

const useInsuranceQuoteData = (
  policyQuote: PolicyQuote,
  responseData: IllustrationResponseData,
  client: Client,
) => {
  const initialPremium = policyQuote.premiumAmount || 0;
  const initialMonthlyBenefit = policyQuote.ltcBenefitMonthlyIndividual || 0;
  const initialAnnualBenefit = policyQuote.ltcBenefitAnnualIndividual || 0;
  const initialRoiData = client
    ? createQuoteWithROIandProjectedCoverage(policyQuote, client)
    : null;
  const initialROI = initialRoiData?.roi || 0;

  let updatedPremium = initialPremium;
  let updatedMonthlyBenefit = initialMonthlyBenefit;
  let updatedAnnualBenefit = initialAnnualBenefit;
  let updatedROI = initialROI;

  if (responseData && client) {
    updatedPremium = responseData.premiumAmount
      ? parseMonetaryValue(responseData.premiumAmount)
      : initialPremium;
    updatedMonthlyBenefit = responseData.initialMonthlyBenBase
      ? parseMonetaryValue(responseData.initialMonthlyBenBase)
      : initialMonthlyBenefit;
    updatedAnnualBenefit = responseData.initialAnnualBenBase
      ? parseMonetaryValue(responseData.initialAnnualBenBase)
      : initialAnnualBenefit;

    const updatedQuote = createUpdatedQuoteFromResponse(policyQuote, responseData);
    const updatedRoiData = createQuoteWithROIandProjectedCoverage(updatedQuote, client);
    updatedROI = updatedRoiData?.roi || initialROI;
  }

  const initialQuoteData = {
    premiumAmount: Math.round(initialPremium),
    monthlyLTCBenefit: Math.round(initialMonthlyBenefit),
    annualLTCBenefit: Math.round(initialAnnualBenefit),
    deathBenefit: Math.round(policyQuote.deathBenefitInitial || 0),
    totalLTCBenefit: Math.round(policyQuote.ltcBenefitTotal || 0),
    expectedROI: initialROI * 100,
  };

  const updatedQuoteData = {
    premiumAmount: Math.round(updatedPremium),
    monthlyLTCBenefit: Math.round(updatedMonthlyBenefit),
    annualLTCBenefit: Math.round(updatedAnnualBenefit),
    deathBenefit: Math.round(
      responseData?.deathBenefit
        ? parseMonetaryValue(responseData.deathBenefit)
        : policyQuote.deathBenefitInitial || 0,
    ),
    totalLTCBenefit: Math.round(
      responseData?.totalInitialLTC
        ? parseMonetaryValue(responseData.totalInitialLTC)
        : policyQuote.ltcBenefitTotal || 0,
    ),
    expectedROI: updatedROI * 100,
  };

  return { initialQuoteData, updatedQuoteData };
};

const useTraditionalLTCQuoteData = (tlcQuote: TraditionalLTCQuote, client: Client | null) => {
  const roiData = client
    ? createTraditionalLTCQuoteWithROIandProjectedCoverage(tlcQuote, client)
    : null;
  const expectedROI = roiData?.roi || 0;

  return { expectedROI };
};

interface UpdatedQuoteSectionProps {
  clientPolicyApplicationId: string;
  sourceQuote: PolicyQuote | TraditionalLTCQuote;
}

export const UpdatedQuoteSection: React.FC<UpdatedQuoteSectionProps> = ({ sourceQuote }) => {
  const client = useSelector(selectClient);
  const quoteId = getQuoteId(sourceQuote);

  const responseData = useSelector((state: AppState) =>
    selectIllustrationResponseDataByQuoteId(state, quoteId),
  ) as IllustrationResponseData;

  const isTraditionalLTC = isTraditionalLTCQuote(sourceQuote);

  const { initialQuoteData, updatedQuoteData } = !isTraditionalLTC
    ? useInsuranceQuoteData(sourceQuote as PolicyQuote, responseData, client)
    : { initialQuoteData: null, updatedQuoteData: null };

  const { expectedROI } = isTraditionalLTC
    ? useTraditionalLTCQuoteData(sourceQuote as TraditionalLTCQuote, client)
    : { expectedROI: 0 };

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
          <RefreshCw className="h-5 w-5 text-gray-500" />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Quote Updated</h3>
            <p className="mt-0.5 text-sm text-gray-600">
              We've refined your initial estimate with a more detailed quote from the carrier
            </p>
          </div>

          {isTraditionalLTC ? (
            <TraditionalLTCQuoteComparison
              quote={sourceQuote as TraditionalLTCQuote}
              expectedROI={expectedROI}
            />
          ) : (
            <InsuranceQuoteComparison
              initialQuote={initialQuoteData!}
              updatedQuote={updatedQuoteData!}
              carrierName={(sourceQuote as PolicyQuote).carrierName}
            />
          )}
        </div>
      </div>
    </div>
  );
};
